import type {
	ProcedureStepComment,
	ProcedureStepEvent,
	ProcedureStepFieldValues,
	ProcedureStepFieldValuesReview,
	Stored,
} from "@overmap-ai/common"
import { memo, useMemo } from "react"

import { ProcedureStepActivityTimelineItemType } from "../../enums/procedures"
import { ProcedureStepCommentActivityTimelineItem } from "./ProcedureStepCommentActivityTimelineItem"
import { ProcedureStepEventActivityTimelineItem } from "./ProcedureStepEventActivityTimelineItem"
import { ProcedureStepFieldValuesActivityTimelineItem } from "./ProcedureStepFieldValuesActivityTimelineItem"
import { ProcedureStepReviewActivityTimelineItem } from "./ProcedureStepReviewActivityTimelineItem"
import type { ProcedureStepAcitvityTimelineItemMapping, ProcedureStepActivityTimelineItem } from "./typings"

interface ProcedureStepActivityTimelineProps {
	procedureStepFieldValues: Stored<ProcedureStepFieldValues>[]
	procedureStepComments: Stored<ProcedureStepComment>[]
	procedureStepEvents: ProcedureStepEvent[]
	procedureStepReviews: Stored<ProcedureStepFieldValuesReview>[]
}

export const ProcedureStepActivityTimeline = memo<ProcedureStepActivityTimelineProps>((props) => {
	const { procedureStepFieldValues, procedureStepComments, procedureStepEvents, procedureStepReviews } = props

	const sortedActivityTimelineItems = useMemo(() => {
		const ret: ProcedureStepActivityTimelineItem[] = []

		const record: { [K in ProcedureStepActivityTimelineItemType]: ProcedureStepAcitvityTimelineItemMapping[K][] } =
			{
				[ProcedureStepActivityTimelineItemType.FIELD_VALUES]: procedureStepFieldValues,
				[ProcedureStepActivityTimelineItemType.COMMENT]: procedureStepComments,
				[ProcedureStepActivityTimelineItemType.EVENT]: procedureStepEvents,
				[ProcedureStepActivityTimelineItemType.REVIEW]: procedureStepReviews,
			}

		for (const [type, items] of Object.entries(record)) {
			ret.push(
				...items.map((item) => ({
					type: type as ProcedureStepActivityTimelineItemType,
					item,
				})),
			)
		}

		const getTimelineItemDate = (item: ProcedureStepActivityTimelineItem["item"]) => {
			if ("submitted_at" in item && item.submitted_at) return item.submitted_at
			return item.created_at ?? item.updated_at ?? ""
		}

		return ret.toSorted(({ item: itemA }, { item: itemB }) => {
			const itemADate = getTimelineItemDate(itemA)
			const itemBDate = getTimelineItemDate(itemB)
			if (itemADate === itemBDate) return 0
			return itemADate < itemBDate ? -1 : 1
		})
	}, [procedureStepComments, procedureStepEvents, procedureStepFieldValues, procedureStepReviews])

	return (
		<div className="flex flex-col gap-4">
			{sortedActivityTimelineItems.map((item) => {
				switch (item.type) {
					case ProcedureStepActivityTimelineItemType.FIELD_VALUES:
						return (
							<ProcedureStepFieldValuesActivityTimelineItem
								key={item.item.uuid}
								procedureStepFieldValues={item.item}
							/>
						)
					case ProcedureStepActivityTimelineItemType.COMMENT:
						return (
							<ProcedureStepCommentActivityTimelineItem
								key={item.item.uuid}
								procedureStepComment={item.item}
							/>
						)
					case ProcedureStepActivityTimelineItemType.EVENT:
						return (
							<ProcedureStepEventActivityTimelineItem
								key={item.item.uuid}
								procedureStepEvent={item.item}
							/>
						)
					case ProcedureStepActivityTimelineItemType.REVIEW:
						return (
							<ProcedureStepReviewActivityTimelineItem
								key={item.item.uuid}
								procedureStepReview={item.item}
							/>
						)
				}
			})}
		</div>
	)
})
ProcedureStepActivityTimeline.displayName = "ProcedureStepActivityTimeline"
